<template>
  <div v-if="showBewModal">
    <SimonModal
      :is-visible="isBEWModalVisible"
      id="bewModal"
      custom-modal-class="bew-modal"
      size="large"
      show-footer-buttons
      :btn-right-title="btnRightTitle"
      show-accordion
      :accordion-title="getSlotText('lblTerms')"
      :accordion-expanded="showTerms"
      @collapseUpdate="collapseUpdate"
      @btnRight="saveSettings"
      @btnLeft="closeModal"
      :loading-right-btn="loading"
      :disabled-btn-right="loading"
      @hide="closeModal"
    >
      <template v-slot:modalTitle>
        <h3>
          <slot name="lblHeadline"></slot>
        </h3>
      </template>
      <template v-slot:accordionText>
        <div class="legal-notes">
          <div v-html="companyInfo"></div>
          <div v-html="usageDataInfo"></div>
          <div v-html="dataTrafficInfo"></div>
          <div v-html="productInfo"></div>
        </div>
      </template>
      <p>
        <slot :name="slotNameBefore"></slot>
      </p>
      <b-form :novalidate="true" v-if="showBew && showData">
        <!-- Case: Both BEW and DAT are true, then show both checkboxes -->
        <b-form-checkbox name="dataConfirmation" id="dataConfirmation" v-model="dataConfirmation" v-if="showData">
          <span v-html="dataHandlingTerms" @click="handleLinkClick"></span>
        </b-form-checkbox>
        <b-form-checkbox name="bewConfirmation" id="bewConfirmation" v-model="bewConfirmation" v-if="showBew">
          <span v-html="bewTerms" @click="handleLinkClick"></span>
        </b-form-checkbox>
      </b-form>
      <div v-else class="single-option">
        <!-- Case: Only BEW or DAT is true, then show only the required text -->
        <span v-html="dataHandlingTerms" @click="handleLinkClick" v-if="showData"></span>
        <span v-html="bewTerms" @click="handleLinkClick" v-else></span>
      </div>
      <span v-html="bewTermsExtra"/>
    </SimonModal>
  </div>
</template>

<script>
import SimonModal from '@/components/modal/SimonModal.vue'
import { wrapQuery } from '@/helper/wrapGQLQuery'
import gql from 'graphql-tag'
import { mapGetters, mapMutations } from 'vuex'
import useFeature from '@/mixins/useFeature'

export default {
  name: 'BewModal',
  components: {
    SimonModal
  },
  mixins: [
    useFeature('showBewModal')
  ],
  data () {
    return {
      showTerms: false,
      dataConfirmation: null,
      bewConfirmation: null,
      loading: false,
      customerConsents: {
        consents: [],
        consentTexts: []
      }
    }
  },
  mounted () {
    if (this.showBewModal) {
      const probingCookie = window.cookieNS.main.getCookie('probing')
      if (probingCookie) {
        return
      }
      if (this.getAggregatedConsents.inquire.adConsent) {
        this.bewConfirmation = true
      }
      if (this.getAggregatedConsents.inquire.passData) {
        this.dataConfirmation = true
      }
      if (this.getLoadingStatus.aggregatedConsents === 'LOADED' && (this.showBew || this.showData)) {
        this.setActiveModal('bew')
        this.setAggregatedConsentsLoadingStatus('')
      }
      window.addEventListener('beforeunload', this.resetModalsState)
    }
  },
  computed: {
    ...mapGetters('modals', [
      'getAggregatedConsents',
      'getActiveModal',
      'getLoadingStatus'
    ]),
    isBEWModalVisible: function () {
      return this.getActiveModal === 'bew' && (this.showBew || this.showData)
    },
    dataHandlingTerms: function () {
      return this.getConsentText('data_handling_terms', 0)
    },
    bewTerms: function () {
      return this.getConsentText('bew_checkout_terms', 0)
    },
    bewTermsExtra: function () {
      return this.getConsentText('bew_checkout_terms', 2)
    },
    companyInfo: function () {
      return this.getConsentText('company_info_modal')
    },
    usageDataInfo: function () {
      return this.getConsentText('usage_data_modal')
    },
    dataTrafficInfo: function () {
      return this.getConsentText('data_traffic_modal')
    },
    productInfo: function () {
      return this.getConsentText('product_info_modal')
    },
    showBew: function () {
      return this.getAggregatedConsents.inquire.adConsent
    },
    showData: function () {
      return this.getAggregatedConsents.inquire.passData
    },
    slotNameBefore: function () {
      let slotName = 'txtBefore'
      if (this.showBew && this.showData) {
        slotName += 'Both'
      } else {
        slotName += 'Single'
        slotName += this.showBew ? 'Bew' : 'Data'
      }
      return slotName
    },
    btnRightTitle () {
      return !this.showBew || !this.showData ? this.getSlotText('btnAgree') : this.getSlotText('btnSave')
    }
  },
  methods: {
    ...mapMutations('modals', [
      'setActiveModal',
      'setAggregatedConsentsLoadingStatus'
    ]),
    getConsentText (name, index = 0) {
      const text = this.customerConsents.consentTexts.find(item => item.name === name)?.text || ''
      return text.split('</p>')[index] || ''
    },
    resetModalsState () {
      this.setActiveModal('')
      this.setAggregatedConsentsLoadingStatus('')
    },
    collapseUpdate (e) {
      this.showTerms = e
    },
    handleLinkClick (e) {
      if (e.target.tagName === 'A' && !this.showTerms) {
        e.preventDefault()
        this.showTerms = true
      }
    },
    closeModal () {
      this.setActiveModal('')
    },
    async saveSettings () {
      this.startLoader()
      this.loading = true

      try {
        await this.$apollo.mutate({
          mutation: gql`
        mutation changeAggregatedConsents($bewConfirmation: Boolean!, $dataConfirmation: Boolean!) {
          changeAggregatedConsents(changeAggregatedConsents: {
            consentsGiven: {
              adConsent: $bewConfirmation,
              passData: $dataConfirmation
            }
          }) {
            success
          }
        }
      `,
          variables: {
            bewConfirmation: this.bewConfirmation !== null ? this.bewConfirmation : false,
            dataConfirmation: this.dataConfirmation !== null ? this.dataConfirmation : false
          }
        })

        this.successToast(this.getSlotText('txtSuccessToastMsg', 'Success'))
        this.closeModal()
      } catch (error) {
        this.errorToast(this.getSlotText('txtCopyErrorToast', 'Hoppla, da ist was schief gelaufen!'))
      } finally {
        this.loading = false
        this.stopLoader()
      }
    }
  },
  apollo: {
    customerConsents: function () {
      return wrapQuery(
        gql`query consent {
          customerConsents {
            consentTexts {
              name
              text
              version
              hash
            }
          }
        }`, this, 'customerConsents', {}, () => {
          this.stopLoader()

          // format consent text
          if (typeof window.consentTextFormatter === 'function') {
            for (let i = 0; i < this.customerConsents.consentTexts.length; i++) {
              this.customerConsents.consentTexts[i].text = window.consentTextFormatter(this.customerConsents.consentTexts[i].name, this.customerConsents.consentTexts[i].text)
            }
          }
        },
        {
          fetchPolicy: 'cache-first'
        },
        () => {
          this.stopLoader()
        }
      )
    }
  }
}
</script>
